import React, { createContext, useState, ReactNode, useEffect } from 'react';
import {
  apiClient
} from '@rti/shared';
import { useAuth } from './AuthContext';
import { useGlobalDataRefresh } from "@/hooks/useGlobalDataRefresh";

interface DataContextType {
  isLoading: boolean;
  isRefreshing: boolean;
  refreshAllData: () => Promise<void>;
  globalRefreshStatus: {
    isRefreshing: boolean;
    lastRefresh: Date | null;
    nextRefresh: Date | null;
    intervalMinutes: number;
  };
  triggerManualRefresh: () => Promise<void>;
  resetGlobalRefresh: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = (): DataContextType => {
  const context = React.useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  // Data state

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get authentication state
  const { isAuthenticated, user } = useAuth();
  const { status: globalRefreshStatus, triggerManualRefresh, refreshManager, reset } = useGlobalDataRefresh();

  // Set up authentication check for the refresh manager (admin-only)
  useEffect(() => {
    refreshManager.setAuthCheck(() => !!user && isAuthenticated && user.is_admin);
  }, [isAuthenticated, user, refreshManager]);

  // Kick off scheduling when an admin logs in; clear when logging out
  useEffect(() => {
    const isAdminAuthed = !!user && isAuthenticated && user.is_admin;
    if (isAdminAuthed) {
      refreshManager.onLogin();
    } else {
      refreshManager.onLogout();
    }
  }, [isAuthenticated, user, refreshManager]);


  const refreshAllData = async (): Promise<void> => {
    try {
      setIsRefreshing(true);

      // Use shared apiClient to perform bulk refresh on the backend/cache
      const result = await apiClient.refreshAllData();
      if (result?.errors && result.errors.length) {
        console.warn('RefreshAllData completed with errors:', result.errors);
      }
      //Refresh the page after data refresh
      window.location.reload();

    } catch (error) {
      console.error('Failed to refresh all data:', error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  };



  const value: DataContextType = {
    isLoading,
    isRefreshing: isRefreshing || globalRefreshStatus.isRefreshing,
    refreshAllData,
    globalRefreshStatus,
    triggerManualRefresh,
    resetGlobalRefresh: reset
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
